import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import logo from './image/logo.png';

export interface ProgramData {
  id: number;
  category: string;
  title: string;
  description: string;
  objective: string;
  exitProfile: string;
  duration: string;
  modality: string;
  level: string;
  tags: string[];
  pensum: string[];
  complementary: string[];
}

const C = {
  white:    '#ffffff',
  bg:       '#f8fafc',
  blue:     '#0ea5e9',
  blueSoft: '#e0f2fe',
  blueText: '#0369a1',
  dark:     '#0f172a',
  mid:      '#475569',
  muted:    '#94a3b8',
  border:   '#e2e8f0',
  indigo:   '#6366f1',
  indigoSoft: '#eef2ff',
};

const s = StyleSheet.create({
  page: {
    backgroundColor: C.white,
    paddingBottom: 52,
  },

  // ── Header ──
  header: {
    paddingHorizontal: 40,
    paddingTop: 28,
    paddingBottom: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 2,
    borderBottomColor: C.blue,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  logo: { width: 40, height: 40 },
  brandName: {
    color: C.dark,
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
  },
  brandSub: {
    color: C.muted,
    fontSize: 7,
    letterSpacing: 0.8,
    marginTop: 2,
  },
  levelPill: {
    backgroundColor: C.blueSoft,
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  levelText: {
    color: C.blueText,
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },

  // ── Title area ──
  titleArea: {
    paddingHorizontal: 40,
    paddingTop: 20,
    paddingBottom: 18,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  categoryLabel: {
    color: C.blue,
    fontSize: 7.5,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  programTitle: {
    color: C.dark,
    fontSize: 19,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1.2,
    marginBottom: 10,
  },
  metaRow: { flexDirection: 'row', gap: 18 },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  metaDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: C.blue },
  metaText: { color: C.mid, fontSize: 8.5 },

  // ── Body ──
  body: { paddingHorizontal: 40, paddingTop: 22 },

  // ── Section ──
  sectionLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 12,
  },
  sectionBar: { width: 3, height: 13, borderRadius: 2, backgroundColor: C.blue },
  sectionTitle: {
    color: C.blueText,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  sectionCount: { color: C.muted, fontSize: 8 },

  // ── Pensum grid ──
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 22,
  },
  chip: {
    width: '31.5%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    backgroundColor: C.bg,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 7,
    padding: 9,
  },
  chipNum: {
    backgroundColor: C.blue,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
    flexShrink: 0,
  },
  chipNumText: {
    color: C.white,
    fontSize: 6.5,
    fontFamily: 'Helvetica-Bold',
  },
  chipName: {
    color: C.dark,
    fontSize: 8,
    lineHeight: 1.45,
    flex: 1,
  },

  // ── Complementary ──
  compRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  compPill: {
    backgroundColor: C.indigoSoft,
    borderRadius: 20,
    paddingHorizontal: 11,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  compStar: { color: C.indigo, fontSize: 7 },
  compText: { color: C.indigo, fontSize: 8, fontFamily: 'Helvetica-Bold' },

  // ── Footer ──
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: C.border,
    paddingHorizontal: 40,
    paddingVertical: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: C.white,
  },
  footerText: { color: C.muted, fontSize: 7.5 },
  footerCenter: { color: C.blue, fontSize: 7.5, fontFamily: 'Helvetica-Bold' },
});

const ProgramPDF: React.FC<{ program: ProgramData }> = ({ program }) => (
  <Document title={program.title} author="CECAP Maicao" subject="Plan de Estudios">
    <Page size="A4" style={s.page}>

      {/* Header */}
      <View style={s.header}>
        <View style={s.logoRow}>
          <Image src={logo} style={s.logo} />
          <View>
            <Text style={s.brandName}>CECAP</Text>
            <Text style={s.brandSub}>Centro de Capacitación · Maicao, La Guajira</Text>
          </View>
        </View>
        <View style={s.levelPill}>
          <Text style={s.levelText}>{program.level}</Text>
        </View>
      </View>

      {/* Title */}
      <View style={s.titleArea}>
        <Text style={s.categoryLabel}>{program.category}</Text>
        <Text style={s.programTitle}>{program.title}</Text>
        <View style={s.metaRow}>
          {[program.duration, program.modality, `${program.pensum.length} módulos`].map((v, i) => (
            <View key={i} style={s.metaItem}>
              <View style={s.metaDot} />
              <Text style={s.metaText}>{v}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Body */}
      <View style={s.body}>

        {/* Pensum */}
        <View style={s.sectionLabel}>
          <View style={s.sectionBar} />
          <Text style={s.sectionTitle}>Plan de Estudios</Text>
          <Text style={s.sectionCount}>  ·  {program.pensum.length} asignaturas</Text>
        </View>
        <View style={s.grid}>
          {program.pensum.map((item, i) => (
            <View key={i} style={s.chip}>
              <View style={s.chipNum}>
                <Text style={s.chipNumText}>{String(i + 1).padStart(2, '0')}</Text>
              </View>
              <Text style={s.chipName}>{item}</Text>
            </View>
          ))}
        </View>

        {/* Complementaria */}
        {program.complementary.length > 0 && (
          <>
            <View style={[s.sectionLabel, { marginTop: 4 }]}>
              <View style={[s.sectionBar, { backgroundColor: C.indigo }]} />
              <Text style={[s.sectionTitle, { color: C.indigo }]}>Formación Complementaria</Text>
            </View>
            <View style={s.compRow}>
              {program.complementary.map((item, i) => (
                <View key={i} style={s.compPill}>
                  <Text style={s.compStar}>★</Text>
                  <Text style={s.compText}>{item}</Text>
                </View>
              ))}
            </View>
          </>
        )}
      </View>

      {/* Footer */}
      <View style={s.footer} fixed>
        <Text style={s.footerText}>cecapmaicao@gmail.com · 300 815 8001</Text>
        <Text style={s.footerCenter}>CECAP MAICAO</Text>
        <Text style={s.footerText}>Cra. 7 #17-36, Barrio San José</Text>
      </View>

    </Page>
  </Document>
);

export default ProgramPDF;
